export enum TokenCapType {
  Legacy = 'standard', // 25 cap (was the old default)
  Standard = 'per-age-low', // 10/15/20 per age caps (new default)
  LowMil = 'low-mil',
  Bump = 'bump',
  // Deprecated: old value for backwards compatibility
  Low = 'low', // was 20 cap, treat as Legacy
}

export enum NoDiceType {
  Standard = 'standard',
  NoDice = 'no-dice',
  NoDicePlus = 'no-dice-plus',
}

export type GameOptions = {
  tradeRowSlide: boolean;
  randomLeader: boolean;
  biggerTradeRow: boolean;
  wipeTradeRow: boolean;
  wipeTributeRow: boolean;
  reshuffleTributesEachAge: boolean;
  warTokens: boolean;
  hideFavor: boolean;
  unbid: boolean;
  retreat1: boolean;
  retreat2: boolean;
  retreat3: boolean;
  tokenCap: TokenCapType;
  noDice: NoDiceType;

  tutorial: boolean;
};
export type GameOptionsKey = keyof GameOptions;

export function getDefaultGameOptions(): GameOptions {
  return {
    tradeRowSlide: false,
    randomLeader: false,
    biggerTradeRow: false,
    wipeTradeRow: false,
    wipeTributeRow: false,
    reshuffleTributesEachAge: false,

    warTokens: false,

    hideFavor: false,

    unbid: true,

    retreat1: false,
    retreat2: false,
    retreat3: false,

    tokenCap: TokenCapType.Standard,
    noDice: NoDiceType.Standard,

    tutorial: false,
  };
}

export type GameOptionDisplayMode = 'devOnly' | 'hidden' | 'visible';
export type GameOptionInfo = {
  name: string;
  key: keyof GameOptions;
  tooltip: string;
  displayMode: GameOptionDisplayMode;
} & (
  | {
      type: 'boolean';
    }
  | {
      type: 'enum';
      valueToDisplayName: Record<string, string>;
    }
);
export function getGameOptionData(): Record<GameOptionsKey, GameOptionInfo> {
  return {
    randomLeader: {
      name: 'Random Leader',
      key: 'randomLeader',
      tooltip: 'Randomly select a starting leader for each player.',
      displayMode: 'visible',
      type: 'boolean',
    },
    tradeRowSlide: {
      name: 'Slide Trade Row',
      key: 'tradeRowSlide',
      tooltip:
        'Remove the rightmost card on the trade row at the end of each turn if it is not drafted.  Keeps trade row fresher.',
      displayMode: 'hidden',
      type: 'boolean',
    },
    biggerTradeRow: {
      name: 'Bigger Trade Row',
      key: 'biggerTradeRow',
      tooltip: 'Increase the size of the trade row by 1 to num players + 2',
      displayMode: 'devOnly',
      type: 'boolean',
    },
    wipeTradeRow: {
      name: 'Wipe Trade Row',
      key: 'wipeTradeRow',
      tooltip:
        'Wipe the trade row at the end of each turn.  Recommended for 2p.',
      displayMode: 'hidden',
      type: 'boolean',
    },
    wipeTributeRow: {
      name: 'Wipe Tribute Row',
      key: 'wipeTributeRow',
      tooltip: 'Wipe the tribute row at the end of each turn.',
      displayMode: 'devOnly',
      type: 'boolean',
    },
    reshuffleTributesEachAge: {
      name: 'Reshuffle Tributes Each Age',
      key: 'reshuffleTributesEachAge',
      tooltip:
        'Shuffle the tribute discard pile back into the deck at each age transition.',
      displayMode: 'visible',
      type: 'boolean',
    },
    warTokens: {
      name: 'War Tokens',
      key: 'warTokens',
      tooltip: 'Enable war tokens.',
      displayMode: 'hidden',
      type: 'boolean',
    },
    hideFavor: {
      name: 'Hide Favor',
      key: 'hideFavor',
      tooltip: 'Hide Favor until the end of the game.',
      displayMode: 'devOnly',
      type: 'boolean',
    },
    unbid: {
      name: 'Unbid',
      key: 'unbid',
      tooltip: 'Allow players to undo their bid.',
      displayMode: 'hidden',
      type: 'boolean',
    },
    retreat1: {
      name: 'Retreat Test 1',
      key: 'retreat1',
      tooltip:
        'Retreat option 1: On a clash loss, refund half your military spent.',
      displayMode: 'devOnly',
      type: 'boolean',
    },
    retreat2: {
      name: 'Retreat Test 2',
      key: 'retreat2',
      tooltip: 'Retreat option 2: On a clash loss, no military is spent.',
      displayMode: 'devOnly',
      type: 'boolean',
    },
    retreat3: {
      name: 'Retreat Test 3',
      key: 'retreat3',
      tooltip:
        "Retreat option 3: If you lose a clash where you bid higher than the winner, you spend M equal to the winner's bid.",
      displayMode: 'devOnly',
      type: 'boolean',
    },
    tokenCap: {
      name: 'Token Cap',
      key: 'tokenCap',
      tooltip: 'Adjust the token cap for the game.',
      displayMode: 'visible',
      type: 'enum',
      valueToDisplayName: {
        [TokenCapType.Standard]: 'Default (10/15/20)',
        [TokenCapType.Legacy]: 'Legacy (25)',
        [TokenCapType.LowMil]: 'Low Military (G:10/15/20, M:5/10/15)',
        [TokenCapType.Bump]: 'Bump (12/16/20)',
      },
    },
    noDice: {
      name: 'No Dice',
      key: 'noDice',
      tooltip: 'Adjust dice rolling behavior for clashes.',
      displayMode: 'visible',
      type: 'enum',
      valueToDisplayName: {
        [NoDiceType.Standard]: 'Standard (roll dice)',
        [NoDiceType.NoDice]: 'No Dice (dice only for ties)',
        [NoDiceType.NoDicePlus]: 'No Dice+ (no dice, ties lose)',
      },
    },
    tutorial: {
      name: 'Tutorial',
      key: 'tutorial',
      tooltip: 'Runs a tutorial.',
      displayMode: 'hidden',
      type: 'boolean',
    },
  };
}

export type NoDiceConfig = {
  skipInitialDiceRolls: boolean;
  rerollToBreakTies: boolean;
};

export const NO_DICE_CONFIGS: Record<NoDiceType, NoDiceConfig> = {
  [NoDiceType.Standard]: {
    skipInitialDiceRolls: false,
    rerollToBreakTies: true,
  },
  [NoDiceType.NoDice]: {
    skipInitialDiceRolls: true,
    rerollToBreakTies: true,
  },
  [NoDiceType.NoDicePlus]: {
    skipInitialDiceRolls: true,
    rerollToBreakTies: false,
  },
};
